/**
 * Timeout and delay constants
 *
 * Centralized timeout values used across the application.
 * All values are in milliseconds unless otherwise noted.
 */

// =============================================================================
// WebSocket Timeouts
// =============================================================================

/**
 * WebSocket heartbeat/ping interval
 */
export const WS_PING_INTERVAL_MS = 30_000;

/**
 * WebSocket ping timeout (how long to wait for pong)
 */
export const WS_PING_TIMEOUT_MS = 60_000;

/**
 * WebSocket reconnect delay
 */
export const WS_RECONNECT_DELAY_MS = 2_000;

/**
 * WebSocket heartbeat interval for client
 */
export const WS_HEARTBEAT_INTERVAL_MS = 30_000;

// =============================================================================
// Session Timeouts
// =============================================================================

/**
 * Session idle timeout (15 minutes)
 */
export const SESSION_IDLE_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Session idle check interval (2 minutes)
 */
export const SESSION_IDLE_CHECK_INTERVAL_MS = 2 * 60 * 1000;

/**
 * Player disconnect grace period (2 minutes)
 */
export const DISCONNECT_GRACE_PERIOD_MS = 120_000;

/**
 * Default round timeout (2 minutes)
 */
export const DEFAULT_ROUND_TIMEOUT_MS = 120_000;

// =============================================================================
// Party/Decision Timeouts
// =============================================================================

/**
 * Party decision sweep interval (1 minute)
 */
export const PARTY_DECISION_SWEEP_MS = 60_000;

/**
 * Maximum age for decisions in memory (2 hours)
 */
export const DECISION_MAX_AGE_MS = 2 * 60 * 60 * 1000;

/**
 * Default decision expiry time (2 minutes)
 */
export const DECISION_EXPIRY_MS = 2 * 60 * 1000;

// =============================================================================
// NPC/Agent Timeouts
// =============================================================================

/**
 * NPC response throttle (minimum time between responses)
 */
export const NPC_RESPONSE_THROTTLE_MS = 3_000;

/**
 * Lock cleanup interval for NPC managers
 */
export const LOCK_CLEANUP_INTERVAL_MS = 10_000;

/**
 * Portrait regeneration retry delay
 */
export const PORTRAIT_REGEN_RETRY_DELAY_MS = 60_000;

// =============================================================================
// Cache Timeouts
// =============================================================================

/**
 * Player character cache TTL (5 minutes)
 */
export const PLAYER_CHAR_CACHE_TTL_MS = 5 * 60 * 1000;

// =============================================================================
// Rate Limiting
// =============================================================================

/**
 * Rate limit window (15 minutes)
 */
export const RATE_LIMIT_WINDOW_MS = 15 * 60 * 1000;

/**
 * Default rate limit window (1 minute)
 */
export const DEFAULT_RATE_LIMIT_WINDOW_MS = 60_000;

// =============================================================================
// UI Timeouts
// =============================================================================

/**
 * Notification auto-dismiss timeout
 */
export const NOTIFICATION_DISMISS_MS = 5_000;

/**
 * Copy feedback timeout (shows "Copied!" indicator)
 */
export const COPY_FEEDBACK_TIMEOUT_MS = 2_000;

/**
 * Connection status clearing timeout
 */
export const CONNECTION_STATUS_TIMEOUT_MS = 2_000;

/**
 * Vote decision auto-resolution timeout
 */
export const VOTE_DECISION_TIMEOUT_MS = 5_000;

/**
 * Navigation timeout on disconnect
 */
export const NAVIGATION_TIMEOUT_MS = 2_000;

// =============================================================================
// Backoff/Retry Configuration
// =============================================================================

/**
 * Exponential backoff base delay
 */
export const BACKOFF_BASE_DELAY_MS = 1_000;

/**
 * Maximum backoff jitter
 */
export const BACKOFF_MAX_JITTER_MS = 1_000;

/**
 * Maximum backoff delay
 */
export const BACKOFF_MAX_DELAY_MS = 4_000;

/**
 * Callback retry delay
 */
export const CALLBACK_RETRY_DELAY_MS = 1_500;
